import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, SectionHeading } from '../components/homepage';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
import {
  marketingCardSx,
  marketingDarkSectionSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

const heroImpactMetrics: Array<{ label: string; value: string; direction: 'up' | 'down' }> = [
  { label: 'Conversion lift', value: '+0.8 pp', direction: 'up' },
  { label: 'Cost reduction', value: '−22 bps', direction: 'down' },
  { label: 'Chargeback rate', value: '−0.3 pp', direction: 'down' },
];

/**
 * Hero artifact — a realistic checkout page mockup showing the payment surface
 * Ormal composes for this buyer. Browser chrome, merchant layout, order summary,
 * actual payment options — with Ormal's optimization signal at the bottom.
 */
const HeroCheckoutPage: React.FC = () => (
  <Box
    sx={{
      border: '1px solid rgba(196,212,232,0.9)',
      borderRadius: 1.5,
      overflow: 'hidden',
      boxShadow: '0 28px 70px rgba(15,23,42,0.09)',
      background: '#fff',
    }}
  >
    {/* Browser chrome */}
    <Box
      sx={{
        px: 1.4, py: 0.85,
        backgroundColor: '#f1f3f5',
        borderBottom: '1px solid rgba(196,212,232,0.8)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.45, flexShrink: 0 }}>
        {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
          <Box key={c} sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: c }} />
        ))}
      </Box>
      <Box
        sx={{
          flex: 1, px: 1.1, py: 0.32,
          borderRadius: 0.45,
          backgroundColor: '#fff',
          border: '1px solid rgba(196,212,232,0.7)',
          display: 'flex', alignItems: 'center', gap: 0.5,
        }}
      >
        <Typography sx={{ color: '#047857', fontSize: '0.58rem', lineHeight: 1 }}>🔒</Typography>
        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontFamily: 'monospace' }}>
          shop.example.com/checkout
        </Typography>
      </Box>
    </Box>

    {/* Page layout: form left, order summary right */}
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 140px' }}>

      {/* Left: checkout form */}
      <Box sx={{ px: 2.2, py: 1.6, borderRight: '1px solid rgba(196,212,232,0.55)' }}>
        <Typography sx={{ color: marketingPalette.headline, fontWeight: 600, fontSize: '1rem', mb: 1.5, letterSpacing: '-0.02em' }}>
          Checkout
        </Typography>

        {/* Delivery — pre-filled for repeat buyer */}
        <Box sx={{ mb: 1.4 }}>
          <Typography sx={{ color: marketingPalette.muted, fontSize: '0.57rem', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.55 }}>
            Delivery
          </Typography>
          <Box
            sx={{
              px: 1, py: 0.6,
              borderRadius: 0.6,
              border: '1px solid rgba(196,212,232,0.7)',
              backgroundColor: '#fafcff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ color: marketingPalette.body, fontSize: '0.75rem' }}>
              Sarah M. · SW1A 1AA · London
            </Typography>
            <Typography sx={{ color: marketingPalette.accent, fontSize: '0.64rem' }}>Change</Typography>
          </Box>
        </Box>

        {/* Payment section */}
        <Box>
          <Typography sx={{ color: marketingPalette.muted, fontSize: '0.57rem', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.7 }}>
            Payment
          </Typography>

          {/* Apple Pay — Ormal puts this first for GB · mobile · repeat */}
          <Box
            sx={{
              px: 1.4, py: 0.92,
              mb: 0.65,
              borderRadius: 0.75,
              backgroundColor: '#000',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
              cursor: 'default',
            }}
          >
            <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
              &#63743; Pay
            </Typography>
            <Box
              sx={{
                position: 'absolute', right: 8,
                px: 0.55, py: 0.18,
                borderRadius: 0.35,
                backgroundColor: 'rgba(255,255,255,0.13)',
                border: '1px solid rgba(255,255,255,0.18)',
              }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'monospace', fontSize: '0.52rem' }}>
                ✦ ormal · wallet-first
              </Typography>
            </Box>
          </Box>

          {/* Or divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.65 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(196,212,232,0.6)' }} />
            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem' }}>or</Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(196,212,232,0.6)' }} />
          </Box>

          {/* Card + Klarna */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.55 }}>
            <Box
              sx={{
                px: 0.9, py: 0.65,
                borderRadius: 0.65,
                border: '1px solid rgba(196,212,232,0.8)',
                backgroundColor: '#fafcff',
                display: 'flex', alignItems: 'center', gap: 0.6,
              }}
            >
              <Box
                sx={{
                  width: 26, height: 16, borderRadius: 0.4,
                  backgroundColor: 'rgba(15,111,255,0.1)',
                  border: '1px solid rgba(15,111,255,0.22)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ color: marketingPalette.accent, fontSize: '0.44rem', fontFamily: 'monospace', fontWeight: 700 }}>VISA</Typography>
              </Box>
              <Typography sx={{ color: marketingPalette.headline, fontSize: '0.76rem' }}>Card</Typography>
            </Box>
            <Box
              sx={{
                px: 0.9, py: 0.65,
                borderRadius: 0.65,
                border: '1px solid rgba(255,182,193,0.5)',
                backgroundColor: 'rgba(255,182,193,0.05)',
                display: 'flex', alignItems: 'center', gap: 0.6,
              }}
            >
              <Box
                sx={{
                  px: 0.5, py: 0.2, borderRadius: 0.3,
                  backgroundColor: '#FFB3C7', flexShrink: 0,
                }}
              >
                <Typography sx={{ color: '#000', fontSize: '0.46rem', fontWeight: 700 }}>Klarna</Typography>
              </Box>
              <Typography sx={{ color: marketingPalette.headline, fontSize: '0.76rem' }}>Pay later</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right: order summary */}
      <Box sx={{ px: 1.5, py: 1.6, backgroundColor: '#f8fafc' }}>
        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.57rem', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
          Order
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.7, mb: 1.2, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 34, height: 34, borderRadius: 0.5, flexShrink: 0,
              backgroundColor: 'rgba(15,111,255,0.08)',
              border: '1px solid rgba(15,111,255,0.14)',
            }}
          />
          <Box>
            <Typography sx={{ color: marketingPalette.headline, fontSize: '0.72rem', lineHeight: 1.3 }}>Leather tote</Typography>
            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem' }}>Camel · ×1</Typography>
          </Box>
        </Box>
        <Box sx={{ borderTop: '1px solid rgba(196,212,232,0.6)', pt: 0.8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.66rem' }}>Subtotal</Typography>
            <Typography sx={{ color: marketingPalette.body, fontSize: '0.66rem' }}>£184.50</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.66rem' }}>Shipping</Typography>
            <Typography sx={{ color: '#047857', fontSize: '0.66rem' }}>Free</Typography>
          </Box>
          <Box sx={{ borderTop: '1px solid rgba(196,212,232,0.6)', mt: 0.8, pt: 0.7, display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ color: marketingPalette.headline, fontSize: '0.74rem', fontWeight: 600 }}>Total</Typography>
            <Typography sx={{ color: marketingPalette.headline, fontSize: '0.74rem', fontWeight: 600 }}>£184.50</Typography>
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Ormal signal strip — route + impact */}
    <Box
      sx={{
        px: { xs: 2, md: 2.3 }, py: 1,
        borderTop: '1px solid rgba(196,212,232,0.9)',
        background: 'linear-gradient(135deg, rgba(15,111,255,0.055), rgba(15,111,255,0.01))',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 1.5,
        alignItems: 'center',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#047857', boxShadow: '0 0 6px rgba(4,120,87,0.55)' }} />
          <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.6rem' }}>
            Ormal · GB · mobile · repeat · 38ms
          </Typography>
        </Box>
        <Typography sx={{ color: marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.6rem' }}>
          Adyen › Visa › frictionless › mid_eu_03
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.8 }}>
        {heroImpactMetrics.map((m) => (
          <Box key={m.label}>
            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.52rem', letterSpacing: '0.07em', textTransform: 'uppercase', mb: 0.12 }}>
              {m.label}
            </Typography>
            <Typography sx={{ color: m.direction === 'up' ? '#047857' : marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
              {m.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

type SignalColumn = {
  stage: string;
  label: string;
  description: string;
  items: { key: string; value: string }[];
};

const decisionPipeline: SignalColumn[] = [
  {
    stage: 'Stage 1',
    label: 'Signals in',
    description:
      'Every intent arrives with a compact request — just what only you can tell us. Everything else Ormal already has on file or derives continuously.',
    items: [
      { key: 'Intent', value: 'Amount, currency, transaction type, country' },
      { key: 'Buyer', value: 'Device type, channel — geo and cross-border derived server-side' },
      { key: 'Risk', value: 'risk_rating from your fraud stack — optional label, not a raw score (optional)' },
      { key: 'Context', value: '5 required fields · 10+ optional · everything else Ormal derives per call' },
    ],
  },
  {
    stage: 'Stage 2',
    label: 'Strategy authored',
    description:
      'An AI strategist weighs every viable path for each customer group — your approval history, market evidence, your contracts and tier positions. Dozens of candidates. One defensible answer, runner-up kept.',
    items: [
      { key: 'Candidates', value: 'Dozens of route × method combinations per customer group' },
      { key: 'Stack state', value: 'Derived live — PSP health, route capacity, tier thresholds' },
      { key: 'Commercial', value: 'On file — MCC, margin, settlement, commitments' },
      { key: 'Scoring', value: 'Conversion × Cost × Risk against your profile, runner-up kept' },
    ],
  },
  {
    stage: 'Stage 3',
    label: 'Stack executed',
    description:
      'The chosen path composes the stack the buyer sees, and the recommendation carries the reasoning the business can review.',
    items: [
      { key: 'Checkout', value: 'Method order + fields + fallbacks, composed per intent' },
      { key: 'Route', value: 'PSP + network + 3DS posture behind the selected method' },
      { key: 'Rationale', value: 'Why this path beat the runner-up, in plain language' },
      { key: 'Feedback', value: 'Outcome writes back into the next round of scoring' },
    ],
  },
];

const controlSurface = [
  {
    label: 'Priority weights',
    body: 'Conversion, payment cost, and risk control are set as weights — not as separate review tracks. Shift the weights and the optimization posture changes across every intent downstream.',
  },
  {
    label: 'Profile switching',
    body: 'Weight profiles (risk-first, growth-push, retention, marketplace-standard) can be swapped without wiping decision history. Switch via API parameter on each call — no re-integration required. Seasonal pushes and tier-close pressure get their own profile instead of one-off overrides.',
  },
  {
    label: 'Review and audit',
    body: 'Every recommendation ships with the runner-up, the signals it read, and the reason it won. Payments, risk, and finance can agree or override from the same evidence.',
  },
];

const ProductPage: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/how-it-works" />
      </Helmet>
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5, md: 6 }, pb: { xs: 5, md: 6 }, overflow: 'hidden' }}>
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3.5, lg: 5 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                  lineHeight: 1,
                  mb: 1.5,
                }}
              >
                Platform
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: marketingDisplayFont,
                  fontSize: { xs: '1.62rem', md: '2.05rem' },
                  fontWeight: 300,
                  letterSpacing: '-0.005em',
                  color: marketingPalette.headline,
                  lineHeight: 1.18,
                  textWrap: 'balance',
                  mb: 1.4,
                }}
              >
                One API call. The full checkout stack, the route, and the reason — returned in under 150ms.
              </Typography>
              <Typography
                sx={{
                  color: marketingPalette.body,
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  lineHeight: 1.65,
                  maxWidth: 560,
                  mb: 2.4,
                }}
              >
                The signals sent in, how every viable path gets scored against your priorities, what comes back — and the narrow set of controls the business actually turns.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                <Button component="a" href={DEMO_REQUEST_MAILTO} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                  Request Demo
                </Button>
                <Button component={RouterLink} to="/resources" sx={marketingSecondaryButtonSx}>
                  See resources
                </Button>
              </Box>

              {/* Proof strip */}
              <Box
                sx={{
                  mt: 3.2,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.4,
                  width: '100%',
                  maxWidth: 520,
                }}
              >
                {[
                  { value: 'One', label: 'Optimized decision per call', detail: 'Method stack + route + MID returned together' },
                  { value: '< 150ms', label: 'Decision latency', detail: 'From intent to executed stack' },
                  { value: 'Per call', label: 'Pricing model', detail: '$0.10/call · not a % of GMV' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      pl: 1.3,
                      borderLeft: '2px solid rgba(15,111,255,0.35)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: marketingDisplayFont,
                        fontSize: { xs: '1.05rem', md: '1.18rem' },
                        fontWeight: 400,
                        color: marketingPalette.accent,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 400, color: marketingPalette.muted, lineHeight: 1.3 }}>
                      {s.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <HeroCheckoutPage />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingDarkSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Decision anatomy"
            title="Three stages turn an incoming intent into an executed stack."
            description="This is what happens between the moment a payment intent arrives and the moment the buyer sees a checkout. Every stage is inspectable. Every answer carries the reasoning behind it."
            marginBottom={3.2}
            inverse
          />
          <Grid container spacing={2}>
            {decisionPipeline.map((column, index) => (
              <Grid item xs={12} md={4} key={column.label}>
                <Card
                  sx={{
                    ...marketingCardSx,
                    height: '100%',
                    p: { xs: 2.2, md: 2.4 },
                    borderTop: '3px solid rgba(15,111,255,0.72)',
                    background: '#ffffff',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 1.15 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 0.8,
                        backgroundColor: marketingPalette.accentSoft,
                        border: '1px solid',
                        borderColor: marketingPalette.accentBorder,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: marketingPalette.accent,
                        fontFamily: 'monospace',
                        fontWeight: 400,
                        fontSize: '0.78rem',
                      }}
                    >
                      {`0${index + 1}`}
                    </Box>
                    <Typography sx={{ color: marketingPalette.accent, fontSize: '0.66rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      {column.stage}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.05rem', md: '1.18rem' }, lineHeight: 1.28, mb: 0.7 }}>
                    {column.label}
                  </Typography>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.62, fontSize: '0.86rem', mb: 1.25 }}>
                    {column.description}
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.65 }}>
                    {column.items.map((item) => (
                      <Box
                        key={item.key}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '92px minmax(0, 1fr)',
                          gap: 0.85,
                          alignItems: 'start',
                          pb: 0.55,
                          borderBottom: '1px dashed rgba(196,212,232,0.9)',
                          '&:last-of-type': { borderBottom: 'none', pb: 0 },
                        }}
                      >
                        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', pt: '2px' }}>
                          {item.key}
                        </Typography>
                        <Typography sx={{ color: marketingPalette.headline, fontSize: '0.78rem', fontWeight: 400, lineHeight: 1.45 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="The recommendation"
            title="One composed stack. The runner-up, the signals, and the reason on the same card."
            description="The output of the pipeline is not a dashboard. It is a reviewable recommendation — the full checkout experience — that payments, risk, and finance can judge from the same card, without hunting for evidence."
            marginBottom={2.5}
          />

          {/* Context contrast — same optimization, different context → different stack */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.4,
              mb: 2.5,
            }}
          >
            {[
              {
                context: ['UK', 'mobile', '£230', 'repeat buyer'],
                stack: ['BNPL', 'Apple Pay', 'Card'],
                route: 'psp_alpha · mid_eu_03',
                rationale: 'BNPL leads on £150+ EU mobile — 18pt lift vs card-first. Wallets stay visible; card is the reliable fallback.',
                accent: '#7c3aed',
                accentSoft: 'rgba(124,58,237,0.06)',
                accentBorder: 'rgba(124,58,237,0.22)',
              },
              {
                context: ['US', 'desktop', '$45', 'first-time buyer'],
                stack: ['Card', 'PayPal', 'Apple Pay'],
                route: 'psp_stripe · mid_us_01',
                rationale: 'Basket below BNPL threshold. Card stays first — familiar, fast on desktop. PayPal and Apple Pay as backup rails.',
                accent: '#0f6fff',
                accentSoft: 'rgba(15,111,255,0.06)',
                accentBorder: 'rgba(15,111,255,0.22)',
              },
            ].map((ex, idx) => (
              <Box
                key={idx}
                sx={{
                  p: { xs: 1.8, md: 2 },
                  borderRadius: 1.25,
                  border: `1px solid ${ex.accentBorder}`,
                  backgroundColor: ex.accentSoft,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.1,
                }}
              >
                {/* Context chips */}
                <Box sx={{ display: 'flex', gap: 0.55, flexWrap: 'wrap', alignItems: 'center' }}>
                  {ex.context.map((tag) => (
                    <Box
                      key={tag}
                      sx={{
                        px: 0.75,
                        py: 0.28,
                        borderRadius: 0.5,
                        border: `1px solid ${ex.accentBorder}`,
                        backgroundColor: '#fff',
                      }}
                    >
                      <Typography sx={{ color: ex.accent, fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 400 }}>
                        {tag}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {/* Method stack */}
                <Box>
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.55 }}>
                    checkout_stack
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, flexWrap: 'wrap' }}>
                    {ex.stack.map((method, i) => (
                      <React.Fragment key={method}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                          <Typography sx={{ color: i === 0 ? ex.accent : marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 400 }}>
                            {`#${i + 1}`}
                          </Typography>
                          <Typography sx={{ color: i === 0 ? ex.accent : marketingPalette.headline, fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 400 }}>
                            {method}
                          </Typography>
                        </Box>
                        {i < ex.stack.length - 1 && (
                          <Typography sx={{ color: marketingPalette.muted, fontSize: '0.75rem', px: 0.1 }}>›</Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
                {/* Route */}
                <Typography sx={{ color: marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 400 }}>
                  {ex.route}
                </Typography>
                {/* Rationale */}
                <Typography sx={{ color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.55 }}>
                  {ex.rationale}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            sx={{
              color: marketingPalette.muted,
              fontSize: '0.74rem',
              lineHeight: 1.55,
              mb: 2.5,
              px: 0.5,
            }}
          >
            The same optimization runs on both. Context — geo, device, basket, buyer cohort — drives the stack, not a segment rule.
          </Typography>

          <Card sx={{ ...marketingCardSx, p: 0, overflow: 'hidden' }}>
            {/* Intent header strip */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) auto' },
                gap: 1.2,
                alignItems: 'center',
                px: { xs: 2, md: 2.4 },
                py: 1.4,
                borderBottom: '1px solid rgba(196,212,232,0.9)',
                background: 'linear-gradient(90deg, rgba(246,249,255,0.9) 0%, rgba(255,255,255,0.9) 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ color: marketingPalette.accent, fontSize: '0.66rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  Intent
                </Typography>
                <Typography sx={{ color: marketingPalette.headline, fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 400 }}>
                  opt_01HKX9ZQ4K2
                </Typography>
                {[
                  { label: 'Amount', value: '$1,840.00' },
                  { label: 'Geo', value: 'GB' },
                  { label: 'Cohort', value: 'Repeat buyer' },
                  { label: 'Class', value: 'checkout' },
                ].map((chip) => (
                  <Box
                    key={chip.label}
                    sx={{
                      px: 0.9,
                      py: 0.35,
                      borderRadius: 0.5,
                      border: '1px solid rgba(196,212,232,0.9)',
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 0.5,
                    }}
                  >
                    <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {chip.label}
                    </Typography>
                    <Typography sx={{ color: marketingPalette.headline, fontSize: '0.76rem', fontWeight: 400 }}>
                      {chip.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#047857', boxShadow: '0 0 8px rgba(4,120,87,0.45)' }} />
                <Typography sx={{ color: '#047857', fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Returned in 38 ms
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={0}>
              {/* Left: the chosen recommendation */}
              <Grid item xs={12} md={7}>
                <Box sx={{ p: { xs: 2.2, md: 2.6 }, borderRight: { md: '1px solid rgba(196,212,232,0.9)' } }}>
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.66rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
                    Chosen stack
                  </Typography>
                  {/* Method order pills */}
                  <Box sx={{ mb: 1.8 }}>
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.82rem', mb: 0.75 }}>
                      Checkout method order
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center' }}>
                      {[
                        { rank: 1, name: 'Apple Pay', primary: true },
                        { rank: 2, name: 'Card', primary: false },
                        { rank: 3, name: 'Klarna', primary: false },
                      ].map((method) => (
                        <Box
                          key={method.name}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.55,
                            px: 1,
                            py: 0.55,
                            borderRadius: 0.6,
                            border: '1px solid',
                            borderColor: method.primary ? 'rgba(15,111,255,0.32)' : 'rgba(196,212,232,0.9)',
                            backgroundColor: method.primary ? 'rgba(15,111,255,0.08)' : '#fff',
                          }}
                        >
                          <Typography sx={{ color: method.primary ? marketingPalette.accent : marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 400 }}>
                            {`#${method.rank}`}
                          </Typography>
                          <Typography sx={{ color: method.primary ? marketingPalette.accent : marketingPalette.headline, fontSize: '0.82rem', fontWeight: 400 }}>
                            {method.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  {/* Route chain */}
                  <Box sx={{ mb: 1.8 }}>
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.82rem', mb: 0.75 }}>
                      Route behind the card method
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {[
                        { label: 'PSP', value: 'Adyen' },
                        { label: 'Network', value: 'Visa' },
                        { label: '3DS', value: 'Frictionless' },
                        { label: 'Settlement', value: 'USD' },
                      ].map((node, idx, arr) => (
                        <React.Fragment key={node.label}>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.55,
                              borderRadius: 0.6,
                              border: '1px solid rgba(196,212,232,0.9)',
                              backgroundColor: '#fbfdff',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.12,
                              minWidth: 72,
                            }}
                          >
                            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1 }}>
                              {node.label}
                            </Typography>
                            <Typography sx={{ color: marketingPalette.headline, fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.2 }}>
                              {node.value}
                            </Typography>
                          </Box>
                          {idx < arr.length - 1 && (
                            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.9rem', fontWeight: 400, px: 0.2 }}>›</Typography>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                  {/* Rationale */}
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 0.8,
                      border: '1px solid rgba(15,111,255,0.18)',
                      backgroundColor: 'rgba(15,111,255,0.05)',
                    }}
                  >
                    <Typography sx={{ color: marketingPalette.accent, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.35 }}>
                      Why this beat the runner-up
                    </Typography>
                    <Typography sx={{ color: marketingPalette.headline, fontSize: '0.86rem', lineHeight: 1.58, fontWeight: 400 }}>
                      Local wallet-first repeat buyers in GB clear this price band without 3DS friction. Apple Pay ahead of Card preserves approval rate, and Adyen scored 4 points higher on cost than the Stripe path without adding risk exposure.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Right: scoring + runner-up */}
              <Grid item xs={12} md={5}>
                <Box sx={{ p: { xs: 2.2, md: 2.4 } }}>
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.66rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>
                    Score against your weights
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, mb: 1.8 }}>
                    {[
                      { label: 'Conversion', weight: 0.45, score: 0.91 },
                      { label: 'Payment cost', weight: 0.30, score: 0.78 },
                      { label: 'Risk control', weight: 0.25, score: 0.82 },
                    ].map((axis) => (
                      <Box key={axis.label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7 }}>
                            <Typography sx={{ color: marketingPalette.headline, fontSize: '0.78rem', fontWeight: 400 }}>
                              {axis.label}
                            </Typography>
                            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.66rem', fontWeight: 400, letterSpacing: '0.04em' }}>
                              {`weight ${Math.round(axis.weight * 100)}%`}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: marketingPalette.accent, fontSize: '0.86rem', fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>
                            {axis.score.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 10, borderRadius: 3, backgroundColor: 'rgba(15,23,42,0.06)', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              width: `${axis.score * 100}%`,
                              background: 'linear-gradient(90deg, #0f6fff 0%, #3ea1ff 100%)',
                              boxShadow: '0 0 8px rgba(15,111,255,0.35)',
                              transition: 'width 400ms ease',
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {/* Runner-up */}
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 0.8,
                      border: '1px dashed rgba(196,212,232,0.9)',
                      backgroundColor: '#fbfdff',
                      mb: 1.4,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Runner-up
                      </Typography>
                      <Typography sx={{ color: '#7c2d12', fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 400 }}>
                        −0.04
                      </Typography>
                    </Box>
                    <Typography sx={{ color: marketingPalette.headline, fontSize: '0.82rem', fontWeight: 400, mb: 0.2 }}>
                      Card → Stripe → Challenge 3DS
                    </Typography>
                    <Typography sx={{ color: marketingPalette.body, fontSize: '0.76rem', lineHeight: 1.5 }}>
                      Close on risk, but lost on cost and the challenge step would have cut approval.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* Show the why — explainability as the close of the recommendation
              story: the case travels with every answer. */}
          <Card sx={{ ...marketingCardSx, mt: 2.5, p: { xs: 2.4, md: 3 } }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: marketingPalette.muted, mb: 1 }}>
              Show the why
            </Typography>
            <Typography sx={{ color: marketingPalette.ink, fontSize: '1.05rem', lineHeight: 1.6, maxWidth: 760 }}>
              Every answer carries its case: the reasoning per method, the runner-up it beat and by
              how much, and the fee economics down to the interchange leg. Payments can defend it to
              finance; finance can defend it to the board. No black box, nothing to take on faith.
            </Typography>
          </Card>
        </Container>
      </Box>

      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Control surface"
            title="Three knobs the business actually turns."
            description="The platform exposes a narrow, deliberate set of controls. No per-rule editor, no sprawling admin surface — the point is to let the team steer posture without re-engineering the pipeline."
            marginBottom={3}
          />
          <Grid container spacing={2.2}>
            {controlSurface.map((item, index) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Card sx={{ ...marketingCardSx, height: '100%', p: { xs: 2.2, md: 2.4 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.85 }}>
                    <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                      {`/${String(index + 1).padStart(2, '0')}`}
                    </Typography>
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '1.04rem', lineHeight: 1.28 }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.7, fontSize: '0.9rem' }}>
                    {item.body}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Integration"
            title="One call. One optimized decision. Returned in under 150ms."
            description="Sent at checkout. Ormal reads the transaction in real time, scores every viable path against your priority weights, and returns the components of that decision — method stack, processor route, MID selection — auditable on every call."
            marginBottom={3}
          />

          {/* Concept flow diagram */}
          <Card sx={{ ...marketingCardSx, p: 0, overflow: 'hidden', mb: 2.5 }}>
            <Box
              sx={{
                px: { xs: 2, md: 2.4 },
                py: 1.25,
                borderBottom: '1px solid rgba(196,212,232,0.9)',
                backgroundColor: 'rgba(246,249,255,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ color: marketingPalette.accent, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                How a transaction resolves
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#047857', boxShadow: '0 0 8px rgba(4,120,87,0.45)' }} />
                <Typography sx={{ color: '#047857', fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                  {'< 150ms'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                p: { xs: 2.2, md: 3 },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 28px minmax(0,1.1fr) 28px minmax(0,1fr)' },
                alignItems: 'start',
              }}
            >
              {/* Col 1: Transaction context */}
              <Box>
                <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.1 }}>
                  Transaction context
                </Typography>
                <Box sx={{ display: 'grid', gap: 0.55 }}>
                  {[
                    { key: 'Intent', value: 'Amount, currency, transaction type, country' },
                    { key: 'Buyer', value: 'Device type, channel — cross-border and geo derived server-side' },
                    { key: 'Risk', value: 'risk_rating from your fraud stack — optional label (low / medium / high / very_high)' },
                    { key: 'Context', value: '5 required fields · 10+ optional · everything else Ormal derives' },
                  ].map((row) => (
                    <Box
                      key={row.key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '68px minmax(0,1fr)',
                        gap: 0.8,
                        pb: 0.5,
                        borderBottom: '1px dashed rgba(196,212,232,0.9)',
                        '&:last-of-type': { borderBottom: 'none', pb: 0 },
                      }}
                    >
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', pt: '2px' }}>
                        {row.key}
                      </Typography>
                      <Typography sx={{ color: marketingPalette.headline, fontSize: '0.78rem', fontWeight: 400, lineHeight: 1.45 }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Arrow 1 */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', pt: 3 }}>
                <Typography sx={{ color: marketingPalette.accent, fontSize: '1.4rem', fontWeight: 300 }}>›</Typography>
              </Box>

              {/* Col 2: Ormal AI profile */}
              <Box
                sx={{
                  mx: { md: 1 },
                  p: { xs: 1.8, md: 2 },
                  borderRadius: 1,
                  border: '1px solid rgba(15,111,255,0.25)',
                  background: 'linear-gradient(135deg, rgba(15,111,255,0.07), rgba(15,111,255,0.02))',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: marketingPalette.accent, fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    Ormal AI profile
                  </Typography>
                  <Box
                    sx={{
                      px: 0.8,
                      py: 0.25,
                      borderRadius: 0.5,
                      border: '1px solid rgba(15,111,255,0.28)',
                      backgroundColor: 'rgba(15,111,255,0.1)',
                    }}
                  >
                    <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                      200+ attributes
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gap: 0.5, mb: 1.2 }}>
                  {[
                    'Your observed approval & dispute history',
                    'Researched market benchmarks per method & corridor',
                    'MID interchange economics',
                    'Contract, commitment & tier position',
                    'Chargeback & liability profile',
                  ].map((attr) => (
                    <Box key={attr} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: marketingPalette.accent, flexShrink: 0 }} />
                      <Typography sx={{ color: marketingPalette.body, fontSize: '0.78rem', lineHeight: 1.45 }}>
                        {attr}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ pt: 1, borderTop: '1px dashed rgba(15,111,255,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Per transaction
                  </Typography>
                  <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 400 }}>
                    Composes the stack
                  </Typography>
                </Box>
              </Box>

              {/* Arrow 2 */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center', pt: 3 }}>
                <Typography sx={{ color: marketingPalette.accent, fontSize: '1.4rem', fontWeight: 300 }}>›</Typography>
              </Box>

              {/* Col 3: Outputs */}
              <Box>
                <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.1 }}>
                  Outputs per transaction
                </Typography>
                <Box sx={{ display: 'grid', gap: 0.85 }}>
                  {[
                    {
                      num: '01',
                      label: 'Method stack',
                      value: '[ BNPL → Apple Pay → Card ]',
                      detail: 'The right options, in the right order.',
                    },
                    {
                      num: '02',
                      label: 'Processor route',
                      value: 'psp_alpha · +22% on this corridor',
                      detail: "Live approval rate, not last week's average.",
                    },
                    {
                      num: '03',
                      label: 'MID selection',
                      value: 'mid_eu_03 · −0.18% iC',
                      detail: 'Selects from your existing merchant accounts — optimized for interchange category and volume tier.',
                    },
                  ].map((output) => (
                    <Box
                      key={output.num}
                      sx={{
                        px: 1.2,
                        py: 1,
                        borderRadius: 0.7,
                        border: '1px solid rgba(196,212,232,0.9)',
                        backgroundColor: '#fff',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.7, mb: 0.25 }}>
                        <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                          {output.num}
                        </Typography>
                        <Typography sx={{ color: marketingPalette.headline, fontSize: '0.82rem', fontWeight: 400 }}>
                          {output.label}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 400, mb: 0.2 }}>
                        {output.value}
                      </Typography>
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.72rem', lineHeight: 1.4 }}>
                        {output.detail}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      px: 1.2,
                      py: 0.8,
                      borderRadius: 0.7,
                      border: '1px dashed rgba(15,111,255,0.25)',
                      backgroundColor: 'rgba(15,111,255,0.04)',
                    }}
                  >
                    <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.04em', mb: 0.15 }}>
                      route_pref + basket_method_swap
                    </Typography>
                    <Typography sx={{ color: marketingPalette.muted, fontSize: '0.68rem' }}>
                      Reason codes on every call. No black box.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Card>

          {/* 3 positioning properties */}
          <Grid container spacing={2}>
            {[
              {
                label: 'Beside the path, not in it',
                body: 'No funds-flow change. No migration. No lock-in. Your existing PSPs execute exactly as before.',
              },
              {
                label: 'Real-time, not retrospective',
                body: 'Every transaction gets the current strategy in under 150ms — and the strategy itself is re-authored as your book and your contracts move. Routing that was configured once and left to drift is exactly what this replaces.',
              },
              {
                label: 'Per-call pricing. Two tiers.',
                body: 'Growth: $500/month + $0.10/call. Enterprise: $2,000/month + $0.10/call — deeper model scoring per call, more candidate paths evaluated. Neither tier is a percentage of your GMV.',
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Card sx={{ ...marketingCardSx, p: { xs: 2, md: 2.2 }, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mb: 0.5 }}>
                    <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.06em' }}>
                      {`/${String(index + 1).padStart(2, '0')}`}
                    </Typography>
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.98rem', lineHeight: 1.3 }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: marketingPalette.body, lineHeight: 1.65, fontSize: '0.88rem' }}>
                    {item.body}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <MarketingFinalCta
        kicker="Get started"
        heading="Find out what's in your payment data."
        body="Share as little as 30 days of transaction history — 90 days gives us more signal. One week later you have a quantified opportunity report — or we tell you honestly there isn't one."
        secondary={{ label: 'See pilot results', to: '/case-studies' }}
      />

      <Footer />
    </Box>
  );
};

export default ProductPage;
